import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppDateField } from '../../components/AppDateField';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ReportPreviewTable } from '../../components/ReportPreviewTable';
import { ScreenHeader } from '../../components/ScreenHeader';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import {
  getReportModuleCapabilities,
  REPORT_ACCESS_PERMISSIONS,
} from '../../config/reportCapabilities';
import { getLocalReportMeta } from '../../config/reportCatalogue';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { runReportPreview } from '../../services/reportService';
import type { ReportPreview, SafeReportFilterState } from '../../types/report';
import {
  createDefaultReportFilterState,
  normalizeReportFilters,
  validateReportFilters,
} from '../../utils/reportFilters';
import { reportErrorMessage } from '../../utils/reportErrors';

type Props = NativeStackScreenProps<RootStackParamList, 'ReportFilter'>;

/** These reports open with data. Preview reapplies the current filters. */
const AUTO_PREVIEW_REPORT_CODES = new Set([
  'EMPLOYEE_DIRECTORY',
  'LATE_ARRIVAL_DEDUCTIONS',
  'LEAVE_BALANCE',
  'MONTHLY_LOP',
]);

export function ReportFilterScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      help: {
        ...typography.caption,
        color: palette.textMuted,
        marginBottom: spacing.lg,
      },
      meta: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      error: { ...typography.body, color: palette.error, marginTop: spacing.sm },
      metaLine: {
        ...typography.caption,
        color: palette.textSecondary,
        marginTop: spacing.lg,
        marginBottom: spacing.sm,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.sm,
      },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getReportModuleCapabilities(auth.permissions);
  const downloads = useReportDownloads();
  const reportCode = route.params.reportCode;
  const meta = getLocalReportMeta(reportCode);

  const autoPreview = Boolean(meta) && AUTO_PREVIEW_REPORT_CODES.has(reportCode);
  const [filters, setFilters] = useState<SafeReportFilterState>(() =>
    createDefaultReportFilterState(reportCode),
  );
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<ReportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(
    autoPreview && capabilities.canPreview,
  );
  const [previewError, setPreviewError] = useState<string | null>(null);
  const requestId = useRef(0);

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.includes(`${reportCode}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads, reportCode],
  );

  const loadPreview = useCallback(
    async (state: SafeReportFilterState) => {
      if (!autoPreview || !capabilities.canPreview) return;
      const currentRequest = ++requestId.current;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await runReportPreview({ reportCode, filters: state });
        if (currentRequest !== requestId.current) return;
        setPreviewResult(result);
      } catch (err) {
        if (currentRequest !== requestId.current) return;
        setPreviewResult(null);
        setPreviewError(reportErrorMessage(err, 'Unable to load this report.'));
      } finally {
        if (currentRequest === requestId.current) setPreviewLoading(false);
      }
    },
    [autoPreview, capabilities.canPreview, reportCode],
  );

  useEffect(() => {
    if (!autoPreview || !capabilities.canPreview) return;
    const defaults = createDefaultReportFilterState(reportCode);
    setFilters(defaults);
    loadPreview(defaults).catch(() => undefined);
  }, [autoPreview, capabilities.canPreview, loadPreview, reportCode]);

  if (!meta) {
    return (
      <PermissionGuard
        anyPermissions={[...REPORT_ACCESS_PERMISSIONS]}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen webLayout="narrow">
          <ErrorState
            message="Report was not found."
            onRetry={() => navigation.goBack()}
          />
        </AppScreen>
      </PermissionGuard>
    );
  }

  const updateField = (field: string, value: string) => {
    setFilters(current => ({ ...current, [field]: value }));
    setError(null);
    setDownloadError(null);
  };

  const ensureValid = (): boolean => {
    const message = validateReportFilters(reportCode, filters);
    if (message) {
      setError(message);
      return false;
    }
    return true;
  };

  const preview = () => {
    if (!capabilities.canPreview) return;
    if (!ensureValid()) return;
    if (autoPreview) {
      loadPreview(filters).catch(() => undefined);
      return;
    }
    navigation.navigate(routes.reportPreview, {
      reportCode,
      filters: { ...filters },
    });
  };

  const exportFormat = async (format: 'CSV' | 'XLSX') => {
    setDownloadError(null);
    if (!ensureValid()) return;
    if (downloads.isDownloadInFlight(reportCode, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }

    try {
      const normalized = normalizeReportFilters(reportCode, filters);
      const result = await downloads.startDownload({
        reportCode,
        reportName: meta.reportName,
        format,
        filters: normalized,
      });
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(
        reportErrorMessage(err, 'The report could not be saved.'),
      );
    }
  };

  return (
    <PermissionGuard
      anyPermissions={[...REPORT_ACCESS_PERMISSIONS]}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen webLayout="narrow" scroll keyboardSafe>
        <ScreenHeader
          title={meta.reportName}
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
          <Text style={themed.help}>
            {meta.description} Select dates from the calendar.
          </Text>

          {meta.filters.length === 0 ? (
            <Text style={themed.meta}>This report has no filters.</Text>
          ) : (
            meta.filters.map(filter =>
              filter.type === 'DATE' ? (
                <AppDateField
                  key={filter.field}
                  label={`${filter.label}${filter.required ? ' *' : ''}`}
                  value={filters[filter.field] ?? ''}
                  onChange={value => updateField(filter.field, value)}
                />
              ) : (
                <AppInput
                  key={filter.field}
                  label={`${filter.label}${filter.required ? ' *' : ''}`}
                  value={filters[filter.field] ?? ''}
                  onChangeText={value => updateField(filter.field, value)}
                  placeholder={
                    filter.type === 'NUMBER' ? 'Number' : undefined
                  }
                  keyboardType={
                    filter.type === 'NUMBER' ? 'number-pad' : 'default'
                  }
                  autoCapitalize="none"
                />
              ),
            )
          )}

          {error ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {error}
            </Text>
          ) : null}
          {downloadError ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {downloadError}
            </Text>
          ) : null}

          <ReportDownloadProgressView
            progress={activeProgress}
            canCancel={Boolean(activeProgress)}
            onCancel={() =>
              activeProgress
                ? downloads.cancelDownload(activeProgress.downloadId)
                : undefined
            }
          />

          <View style={styles.actions}>
            {capabilities.canPreview ? (
              <AppButton title="Preview" onPress={preview} />
            ) : null}
            {capabilities.canExportCsv ? (
              <AppButton
                title="Download CSV"
                variant="secondary"
                loading={downloads.isDownloadInFlight(reportCode, 'CSV')}
                disabled={downloads.isDownloadInFlight(reportCode, 'CSV')}
                onPress={() => exportFormat('CSV')}
              />
            ) : null}
            {capabilities.canExportXlsx ? (
              <AppButton
                title="Download XLSX"
                variant="secondary"
                loading={downloads.isDownloadInFlight(reportCode, 'XLSX')}
                disabled={downloads.isDownloadInFlight(reportCode, 'XLSX')}
                onPress={() => exportFormat('XLSX')}
              />
            ) : null}
          </View>

          {autoPreview && capabilities.canPreview ? (
            <View style={styles.results}>
              {previewLoading && !previewResult ? (
                <AppLoader message="Loading report" />
              ) : null}
              {previewError && !previewResult ? (
                <ErrorState
                  message={previewError}
                  onRetry={() => loadPreview(filters)}
                />
              ) : null}
              {previewResult ? (
                <>
                  <Text style={themed.metaLine}>
                    Rows: {previewResult.totalRows}
                    {previewResult.truncated ? ' (preview truncated)' : ''}
                  </Text>
                  {previewResult.truncated ? (
                    <Text accessibilityRole="alert" style={themed.warning}>
                      Showing a limited preview. Download an export for the full
                      result.
                    </Text>
                  ) : null}
                  {previewError ? (
                    <ErrorState
                      message={previewError}
                      onRetry={() => loadPreview(filters)}
                    />
                  ) : null}
                  <ReportPreviewTable
                    columns={previewResult.columns}
                    rows={previewResult.rows}
                  />
                </>
              ) : null}
            </View>
          ) : null}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  actions: { gap: spacing.sm, marginTop: spacing.xl },
  results: { marginTop: spacing.lg },
});
